import { useState } from 'react';

import type { App } from '@keboola/api-client/developerPortal/types';
import { AlertModal, Button, Modal, setSubmitError, toast, useAppForm } from '@keboola/design';

import { getApiErrorMessage } from '../../auth/errors';
import { formValidators } from '../../auth/validate';
import { usePublishApp, useRejectApp } from '../queries';
import { rejectAppFormSchema } from '../schemas';

const FORM_ID = 'reject-app';

/**
 * Publish, and — when there is a request outstanding — reject.
 *
 * - Reject needs `publishingStatus === 'requested'`; its reason is written onto that request.
 * - Publish needs no request.
 */
export function PublishRejectActions({ app }: { app: App }) {
  const publish = usePublishApp();

  if (app.isPublic === true || app.isDeprecated === true) return null;

  return (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
      <AlertModal
        // Caught here: `AlertModal` closes on settle either way, so a rejection would fail silently.
        onConfirm={async () => {
          try {
            await publish.mutateAsync(app.id);
            toast.success(`${app.name} is now public.`);
          } catch (error) {
            toast.error(getApiErrorMessage(error, `Could not publish ${app.name}.`));
          }
        }}
      >
        <AlertModal.Trigger asChild>
          <Button variant="secondary" size="small">
            Publish
          </Button>
        </AlertModal.Trigger>
        <AlertModal.Content
          variant="info"
          title={`Publish ${app.name}?`}
          confirmLabel="Publish component"
        >
          It becomes visible in the public catalog and available to every Keboola project.
        </AlertModal.Content>
      </AlertModal>

      {app.publishingStatus === 'requested' && <RejectModal app={app} />}
    </div>
  );
}

function RejectModal({ app }: { app: App }) {
  const [open, setOpen] = useState(false);
  const reject = useRejectApp();

  const form = useAppForm({
    defaultValues: { reason: '' },
    validators: formValidators(rejectAppFormSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        await reject.mutateAsync({ appId: app.id, reason: value.reason });
        toast.success(`The publishing request for ${app.name} was rejected.`);
        setOpen(false);
        form.reset();
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'Could not reject the request.'));
      }
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) form.reset();
      }}
    >
      <Modal.Trigger asChild>
        <Button variant="danger" size="small">
          Reject
        </Button>
      </Modal.Trigger>
      <Modal.Content size="medium">
        <Modal.Header description={`Publishing request for ${app.id}.`}>
          Reject component publishing
        </Modal.Header>
        <Modal.Body>
          <form.AppForm>
            <form.FormBody id={FORM_ID}>
              <form.AppField name="reason">
                {(field) => (
                  <field.TextareaField
                    label="Rejection reason"
                    required
                    placeholder="What does the vendor need to change?"
                    help="Shown to the vendor on their component page — this is the only feedback they get."
                  />
                )}
              </form.AppField>
              <form.SubmitErrorAlert />
            </form.FormBody>
          </form.AppForm>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Modal.Close>
          <form.AppForm>
            <form.SubmitButton form={FORM_ID}>Reject request</form.SubmitButton>
          </form.AppForm>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
